import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { createContext, useCallback, useContext, useState } from "react";

export interface ConfirmDialogProps {
  title?: string;
  /**
   * Accepts a node, not just a string, so a caller whose message IS a list of changes can render it
   * as one — struck-through old value, blue-lined new value — instead of flattening it to text. A
   * plain string still works and is still the common case.
   */
  message?: React.ReactNode;
  okButtonText?: string;
  cancelButtonText?: string;
  alternativeOkText?: string;
}
export enum ConfirmResponse {
  Ok,
  AltOk,
  Cancel
}
export type ConfirmDialogType = (props: ConfirmDialogProps) => Promise<ConfirmResponse>;

export const ConfirmDialogContext = createContext<ConfirmDialogType>(undefined!);

export default function ConfirmDialogProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [promise, setPromise] = useState<{ resolve: (value: ConfirmResponse) => void } | null>(null);
  const [title, setTitle] = useState<string>('');
  const [message, setMessage] = useState<React.ReactNode>('');
  const [okButtonText, setOkButtonText] = useState<string>('Ok');
  const [cancelButtonText, setCancelButtonText] = useState<string>('Cancel');
  const [alternativeOkText, setAlternativeOkText] = useState<string>('');

  const confirm = useCallback((props: ConfirmDialogProps) => new Promise((resolve: (value: ConfirmResponse) => void, _reject) => {
    setTitle(props.title ?? 'Confirm');
    setMessage(props.message ?? 'Are you sure?');
    setOkButtonText(props.okButtonText ?? 'Ok');
    setCancelButtonText(props.cancelButtonText ?? 'Cancel');
    setAlternativeOkText(props.alternativeOkText ?? '');
    setPromise({ resolve });
  }), []);

  const handleClose = () => {
    setPromise(null);
    setTitle('');
    setMessage('');
    setOkButtonText('Ok');
    setCancelButtonText('Cancel');
    setAlternativeOkText('');
  };

  const handleConfirm = () => {
    promise?.resolve(ConfirmResponse.Ok);
    handleClose();
  };

  const handleAltConfirm = () => {
    promise?.resolve(ConfirmResponse.AltOk);
    handleClose();
  }

  const handleCancel = () => {
    promise?.resolve(ConfirmResponse.Cancel);
    handleClose();
  };

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      <Dialog open={promise !== null} fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {/* `component="div"` because a rich message legally nests block content, which a <p> may
              not; `pre-wrap` because these messages are newline-and-bullet formatted and the default
              collapsed every list into one run-on paragraph. */}
          <DialogContentText component="div" sx={{ whiteSpace: "pre-wrap" }}>
            {message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirm}>{okButtonText}</Button>
          {alternativeOkText.length > 0 && (
            <Button onClick={handleAltConfirm}>{alternativeOkText}</Button>
          )}
          <Button onClick={handleCancel}>{cancelButtonText}</Button>
        </DialogActions>
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const context = useContext(ConfirmDialogContext);
  if (context === undefined) {
    throw new Error("useConfirmDialog must be used within a ConfirmDialogProvider");
  }
  return context;
}
